import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Alert,
  Tabs,
  Tab,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Divider,
  Paper
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { templateService } from '../services/api';
import { PROJECT_DOMAINS, TEAM_ROLES, ACCESS_LEVELS } from '../constants/projectOptions';

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Basse' },
  { value: 'medium', label: 'Moyenne' },
  { value: 'high', label: 'Haute' },
  { value: 'urgent', label: 'Urgente' }
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Actif' },
  { value: 'on_hold', label: 'En pause' },
  { value: 'completed', label: 'Terminé' }
];

const COLOR_OPTIONS = [
  { value: '#3498db', label: 'Bleu' },
  { value: '#27ae60', label: 'Vert' },
  { value: '#e74c3c', label: 'Rouge' },
  { value: '#f39c12', label: 'Orange' },
  { value: '#9b59b6', label: 'Violet' },
  { value: '#1abc9c', label: 'Turquoise' },
  { value: '#34495e', label: 'Gris foncé' }
];

function ProjectForm({ open, onClose, onSubmit, project, isEdit }) {
  const [activeTab, setActiveTab] = useState(0); // 0: Blank, 1: Template
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    priority: 'medium',
    status: 'active',
    color: '#3498db',
    domain: ''
  });
  
  // Members to invite
  const [members, setMembers] = useState([]);
  const [newMember, setNewMember] = useState({
    email: '',
    role: 'editor',
    functionalRole: ''
  });

  const [error, setError] = useState('');

  useEffect(() => {
    if (project && isEdit) {
      setFormData({
        title: project.title || '',
        description: project.description || '',
        start_date: project.start_date ? project.start_date.split('T')[0] : '',
        end_date: project.end_date ? project.end_date.split('T')[0] : '',
        priority: project.priority || 'medium',
        status: project.status || 'active',
        color: project.color || '#3498db',
        domain: project.domain || ''
      });
      // We don't load existing members here for editing, 
      // as member management is usually in a separate "Settings" or "Team" view.
      // But for creation, we allow adding initial members.
    } else {
      setFormData({
        title: '',
        description: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        priority: 'medium',
        status: 'active',
        color: '#3498db',
        domain: ''
      });
      setMembers([]);
      // Load templates only for new projects
      loadTemplates();
    }
    setError('');
    setActiveTab(0);
    setSelectedTemplate(null);
    setNewMember({ email: '', role: 'editor', functionalRole: '' });
  }, [project, isEdit, open]);

  const loadTemplates = async () => {
    try {
      const response = await templateService.getAll();
      setTemplates(response.data);
    } catch (err) {
      console.error('Failed to load templates', err);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setFormData({
      ...formData,
      title: template.title, // Pre-fill title
      description: template.description // Pre-fill description
    });
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Le titre est requis');
      return false;
    }

    if (formData.title.trim().length < 3) {
      setError('Le titre doit contenir au moins 3 caractères');
      return false;
    }

    if (activeTab === 0 && formData.start_date && formData.end_date) {
      if (new Date(formData.start_date) > new Date(formData.end_date)) {
        setError('La date de fin doit être après la date de début');
        return false;
      }
    }

    if (activeTab === 1 && !selectedTemplate) {
      setError('Veuillez sélectionner un template');
      return false;
    }

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (activeTab === 1 && selectedTemplate) {
      // Handle template submission
      // pass templateId separately or handle in parent
      onSubmit({
        ...formData,
        templateId: selectedTemplate.id,
        isTemplate: true,
        domain: formData.domain, // Keep domain if selected
        members
      });
    } else {
      onSubmit({
        ...formData,
        members
      });
    }
  };

  const handleAddMember = () => {
    if (!newMember.email || !/\S+@\S+\.\S+/.test(newMember.email)) {
      setError('Email invalide');
      return;
    }
    if (members.some(m => m.email === newMember.email)) {
      setError('Cet email est déjà dans la liste');
      return;
    }
    setMembers([...members, newMember]);
    setNewMember({ email: '', role: 'editor', functionalRole: '' });
    setError('');
  };

  const handleRemoveMember = (email) => {
    setMembers(members.filter(m => m.email !== email));
  };

  const getIcon = (iconName) => {
    // Simple mapping or return a default icon
    return '📁'; 
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {isEdit ? 'Modifier le projet' : 'Créer un nouveau projet'}
        </DialogTitle>

        <DialogContent>
          {!isEdit && (
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                <Tab label="Projet vide" />
                <Tab label="À partir d'un modèle" />
              </Tabs>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {activeTab === 1 && !isEdit ? (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {templates.map((template) => (
                <Grid item xs={12} sm={6} key={template.id}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      height: '100%',
                      borderColor: selectedTemplate?.id === template.id ? 'primary.main' : 'divider',
                      bgcolor: selectedTemplate?.id === template.id ? 'action.selected' : 'background.paper'
                    }}
                  >
                    <CardActionArea onClick={() => handleTemplateSelect(template)} sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography variant="h5" sx={{ mr: 1 }}>{getIcon(template.icon)}</Typography>
                          <Typography variant="h6" component="div">
                            {template.title}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {template.category}
                        </Typography>
                        <Typography variant="body2">
                          {template.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : null}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Titre du projet"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                autoFocus
                helperText={activeTab === 1 ? "Vous pouvez modifier le titre du projet généré" : ""}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date de début"
                name="start_date"
                type="date"
                value={formData.start_date}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {activeTab === 0 && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date de fin"
                  name="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priorité</InputLabel>
                <Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  label="Priorité"
                >
                  {PRIORITY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {isEdit && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    label="Statut"
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {activeTab === 0 && (
              <Grid item xs={12} sm={isEdit ? 12 : 6}>
                <FormControl fullWidth>
                  <InputLabel>Couleur</InputLabel>
                  <Select
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    label="Couleur"
                    renderValue={(value) => (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            backgroundColor: value
                          }}
                        />
                        {COLOR_OPTIONS.find(c => c.value === value)?.label}
                      </Box>
                    )}
                  >
                    {COLOR_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              backgroundColor: option.value
                            }}
                          />
                          {option.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>

          {!isEdit && (
            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonAddIcon sx={{ mr: 1 }} />
                Inviter des membres (optionnel)
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Email"
                      value={newMember.email}
                      onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                      placeholder="collègue@exemple.com"
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Rôle (Accès)</InputLabel>
                      <Select
                        value={newMember.role}
                        label="Rôle (Accès)"
                        onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                      >
                        {ACCESS_LEVELS.map((level) => (
                          <MenuItem key={level.value} value={level.value}>{level.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Fonction</InputLabel>
                      <Select
                        value={newMember.functionalRole}
                        label="Fonction"
                        onChange={(e) => setNewMember({ ...newMember, functionalRole: e.target.value })}
                      >
                        <MenuItem value=""><em>Aucun</em></MenuItem>
                        {TEAM_ROLES.map((role) => (
                          <MenuItem key={role} value={role}>{role}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button 
                      variant="outlined" 
                      startIcon={<AddIcon />} 
                      fullWidth 
                      onClick={handleAddMember}
                      disabled={!newMember.email}
                    >
                      Ajouter
                    </Button>
                  </Grid>
                </Grid>

                {members.length > 0 && (
                  <List dense sx={{ mt: 2 }}>
                    {members.map((member, index) => (
                      <ListItem key={index} divider={index !== members.length - 1}>
                        <ListItemText
                          primary={member.email}
                          secondary={`${ACCESS_LEVELS.find(l => l.value === member.role)?.label} • ${member.functionalRole || 'Sans fonction'}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" size="small" onClick={() => handleRemoveMember(member.email)}>
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onClose}>Annuler</Button>
          <Button type="submit" variant="contained">
            {isEdit ? 'Mettre à jour' : (activeTab === 1 ? 'Créer depuis le modèle' : 'Créer')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default ProjectForm;